import { state } from './state.js';
import { socket, serverOnline } from './socket.js';

export type MandataryStatus = 'pending' | 'active' | 'suspended' | 'rejected';
export type MandataryKind = 'individual' | 'business' | 'association' | 'public_body';

export type MandataryProfile = {
  status: MandataryStatus;
  kind: MandataryKind;
  displayName: string;
  contactEmail: string;
  createdAt: number | null;
  approvedAt: number | null;
  termsAcceptedAt: number | null;
  termsVersion: string | null;
  motivation?: string;
  submittedAt?: number | null;
  reviewedAt?: number | null;
  reviewNote?: string;
};

const STORAGE_KEY = 'guild_mandataryProfile';

function setProfile(profile: MandataryProfile | null) {
  (state as any).mandataryProfile = profile;
  try {
    if (profile) localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {}
  window.dispatchEvent(new CustomEvent('guild-mandatary-profile', { detail: profile }));
}

export function loadMandataryProfileFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    setProfile(raw ? JSON.parse(raw) : null);
  } catch {
    setProfile(null);
  }
}

export function clearMandataryProfile() {
  setProfile(null);
}

export function currentMandataryProfile(): MandataryProfile | null {
  return (state as any).mandataryProfile || null;
}

export function isActiveMandatary() {
  return currentMandataryProfile()?.status === 'active';
}

export function syncMandataryProfile() {
  return new Promise<MandataryProfile | null>((resolve) => {
    if (!serverOnline.value || !socket?.connected) {
      resolve(currentMandataryProfile());
      return;
    }
    socket.emit('getMandataryProfile', (res: any) => {
      if (!res?.error) setProfile(res?.profile || null);
      resolve(res?.error ? currentMandataryProfile() : (res?.profile || null));
    });
  });
}

export function submitMandataryApplication(payload: {
  kind?: MandataryKind;
  displayName?: string;
  contactEmail?: string;
  motivation: string;
  termsAccepted: true;
  termsVersion: string;
}) {
  return new Promise<{ success: boolean; profile?: MandataryProfile; error?: string }>((resolve) => {
    if (!serverOnline.value || !socket?.connected) {
      resolve({ success: false, error: 'Le serveur doit être disponible pour envoyer la candidature.' });
      return;
    }
    socket.emit('submitMandataryApplication', payload, (res: any) => {
      if (res?.success && res.profile) setProfile(res.profile);
      resolve(res || { success: false, error: 'Candidature mandataire impossible.' });
    });
  });
}

export const activateMandataryProfile = submitMandataryApplication;

socket.on('mandataryApplicationUpdated', (payload: any) => {
  if (payload?.profile) setProfile(payload.profile);
});
